import { switchMap, first, tap, delay, distinctUntilChanged, startWith, map, shareReplay } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { isEqual } from 'lodash';
import gql from 'graphql-tag';
import { Observable } from 'rxjs';

import { SystemType } from '../models';
import { SystemsService } from '../systems/systems.service';
import { OnConnextAPISubscription } from '../telemetry/index.graph-ql';
import { isNotNullOrUndefined } from '../utils';

const responseQuery = gql`
  fragment connextapires on ConnexTAPIRes {
  deviceId
}`;

@Injectable({
  providedIn: 'root',
})
export class SystemEventsService {
  private readonly subscriptions = new Map<string, Observable<any>>();

  constructor(
    private readonly onConnextAPISubscription: OnConnextAPISubscription,
    private readonly systemsService: SystemsService,
  ) {}

  onUnknownSystemEvent(deviceId: string): Observable<any> {
    if (this.subscriptions.has(deviceId)) {
      return this.subscriptions.get(deviceId)!;
    }

    const connextEvent$ = this.onConnextAPISubscription.execute(responseQuery, { deviceId }).pipe(
      first(),
      switchMap((d: any) => d),
      startWith(false),
      tap((d: any) => console.warn(d)),
    );

    this.subscriptions.set(deviceId, connextEvent$);
    return this.subscriptions.get(deviceId)!;
  }

  onSystemEvent(type: SystemType, uid: string): Observable<any> {
    if (this.subscriptions.has(uid)) {
      return this.subscriptions.get(uid)!;
    }

    const event$ = this.systemsService.systems$.pipe(
      isNotNullOrUndefined(),
      distinctUntilChanged((prev, curr) => {
        const prevS = prev?.find((s) => s.uid === uid);
        const currS = curr?.find((s) => s.uid === uid);
        return isEqual(prevS, currS) === true;
      }),
      map((systems) => systems.find((s) => s.uid === uid)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    if (type === 'connext') {
      const connextEvent$ = event$.pipe(
        map((system) => system?.configuration.connextId!),
        switchMap((deviceId) => this.onConnextAPISubscription.execute(responseQuery, { deviceId }).pipe(
          first(),
          switchMap((d: any) => d),
          startWith(false),
          tap((d: any) => console.warn(d)),
        )),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
      this.subscriptions.set(uid, connextEvent$);
      return this.subscriptions.get(uid)!;
    }

    this.subscriptions.set(uid, event$);
    return this.subscriptions.get(uid)!;
  }
}
